/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react'
import { getBrands, getRecommendations, getRecommendationsByAttributes, getFilterMap, addProfileBrand } from '../api'
import { useAuth } from '../context/AuthContext'
import { useProfiles } from '../context/ProfileContext'
import BrandCard from '../components/BrandCard'
import './Discover.css'
import SuggestBrandModal from '../components/SuggestBrandModal'

const MAX_COLD_START_PER_TIER = 20
const LAST_PROFILE_KEY = 'marqueindex_last_profile'
const EXPLORE = 'Explore'
const NEWLY_ADDED = 'Newly Added'
const NEWLY_ADDED_COUNT = 15

// Find-by-attributes: outward-facing axis dropdowns. Options map to score buckets.
const ATTR_AXES = [
    { field: 'construction_philosophy', label: 'Construction', options: [['low', 'Lower priority'], ['mid', 'Neutral'], ['high', 'Higher priority']] },
    { field: 'formality_register', label: 'Formality', options: [['low', 'Informal'], ['mid', 'Neutral'], ['high', 'Formal']] },
    { field: 'fit_language', label: 'Fit', options: [['low', 'Oversized'], ['mid', 'True to size'], ['high', 'Slim']] },
    { field: 'utility_function', label: 'Functionality', options: [['low', 'Style-first'], ['mid', 'Neutral'], ['high', 'Practical & functional']] },
    { field: 'attitude_loudness', label: 'Design', options: [['low', 'Minimalist'], ['mid', 'Neutral'], ['high', 'Maximalist']] },
    { field: 'tradition_trendiness', label: 'Trendiness', options: [['low', 'Traditional'], ['mid', 'Neutral'], ['high', 'Trendy']] },
]

// Deterministic shuffle seeded by today's date — stable all day, reshuffles at midnight,
// identical for every user on a given day. Used for the Explore browse order.
function dailyShuffle(arr) {
    const today = new Date()
    let seed = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate()
    const rng = () => {
        seed = (seed * 9301 + 49297) % 233280
        return seed / 233280
    }
    const a = [...arr]
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1))
        ;[a[i], a[j]] = [a[j], a[i]]
    }
    return a
}

// Lightweight GA4 event helper — no-ops if gtag isn't loaded
function track(event, params = {}) {
    if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', event, params)
    }
}

function saveLastProfile(profileId) {
    try {
        localStorage.setItem(LAST_PROFILE_KEY, profileId === null ? '__scratch__' : profileId)
    } catch {}
}

function loadLastProfile() {
    try {
        const val = localStorage.getItem(LAST_PROFILE_KEY)
        if (val === null) return undefined // never set
        if (val === '__scratch__') return null // scratch
        return val // profile ID
    } catch {
        return undefined
    }
}

function getStorageKey(profileId) {
    return profileId ? `marqueindex_discover_${profileId}` : 'marqueindex_discover_scratch'
}

function saveDiscoverState(profileId, selectedBrands, recommendations, hasSearched) {
    try {
        const key = getStorageKey(profileId)
        const data = {
            selectedBrandIds: selectedBrands.map(b => b.id),
            recommendationIds: recommendations.map(r => r.brand.id),
            scores: Object.fromEntries(recommendations.map(r => [r.brand.id, r.score])),
            hasSearched
        }
        localStorage.setItem(key, JSON.stringify(data))
    } catch {}
}

function loadDiscoverState(profileId) {
    try {
        const key = getStorageKey(profileId)
        const stored = localStorage.getItem(key)
        return stored ? JSON.parse(stored) : null
    } catch {
        return null
    }
}

function Discover() {
    const { user } = useAuth()
    const {
        activeProfileId,
        activeProfile,
        defaultProfile,
        profiles,
        likedBrandIds,
        dislikedBrandIds,
        saveBrandsToProfile,
        brandsLoadVersion,
        loadedForProfileId,
        reloadProfileBrands,
        canCreateCustomProfile,
        createProfile,
        switchProfile
    } = useProfiles()

    const [allBrands, setAllBrands] = useState([])
    const [filterGroups, setFilterGroups] = useState({})
    const [showSuggest, setShowSuggest] = useState(false)
    const [excludedThemes, setExcludedThemes] = useState([])
    const [hideThemesOpen, setHideThemesOpen] = useState(false)
    const hideThemesRef = useRef(null)
    const [discoverMode, setDiscoverMode] = useState('brands') // 'style' or 'brands'
    const [selectedThemes, setSelectedThemes] = useState([])
    const [selectedBrands, setSelectedBrands] = useState([])
    const [recommendations, setRecommendations] = useState([])
    const [searchTerm, setSearchTerm] = useState('')
    const [searchResults, setSearchResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [brandsAddedSinceSearch, setBrandsAddedSinceSearch] = useState(0)
    const [activeTheme, setActiveTheme] = useState('')
    const [coldStartPage, setColdStartPage] = useState(1)
    // Find-by-attributes state
    const [attrAxes, setAttrAxes] = useState({}) // { field: 'low'|'mid'|'high' }
    const [attrThemes, setAttrThemes] = useState(['', '', '']) // up to 3 theme selections
    const [lastSearchWasAttr, setLastSearchWasAttr] = useState(false)
    const [attrOpen, setAttrOpen] = useState(false)
    // By Style tab keeps its own independent results/searched state
    const [attrResults, setAttrResults] = useState([])
    const [attrHasSearched, setAttrHasSearched] = useState(false)
    const [attrLoading, setAttrLoading] = useState(false)
    const themeBrandOrderRef = useRef({})

    useEffect(() => {
        function handleOutsideClick(e) {
            if (hideThemesRef.current && !hideThemesRef.current.contains(e.target)) {
                setHideThemesOpen(false)
            }
        }
        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [])
    const coldStartOrderRef = useRef({}) // cached ordered list per theme (all brands, unfiltered by selection)
    const [visibleColdStart, setVisibleColdStart] = useState([]) // stable displayed pills
    const [includeDefaultDislikes, setIncludeDefaultDislikes] = useState(false)
    const [saveMessage, setSaveMessage] = useState('')
    const [showNewProfileModal, setShowNewProfileModal] = useState(false)
    const [newProfileName, setNewProfileName] = useState('')
    const [newProfileError, setNewProfileError] = useState('')
    const [showSavePicker, setShowSavePicker] = useState(false)
    const lastHandledVersion = useRef(0)
    const hasMountedRef = useRef(false)
    const restoredFromCache = useRef(false)

    useEffect(() => {
        track('discover_landed')
        getBrands().then(brands => setAllBrands(brands || []))
        getFilterMap().then(data => {
            const groups = data || {}
            setFilterGroups(groups)
            const firstTheme = Object.keys(groups).sort()[0]
            if (firstTheme) setActiveTheme(EXPLORE)
        })
    }, [])

    // Profile selection is now restored by ProfileContext on init
    // Just save it whenever it changes
    useEffect(() => {
        saveLastProfile(activeProfileId)
    }, [activeProfileId])

    // On mount: try to restore from localStorage first, otherwise sync from profile
    useEffect(() => {
        if (hasMountedRef.current) return
        if (allBrands.length === 0) return

        hasMountedRef.current = true

        // Try restoring cached discover state for THIS profile
        // Use activeProfileId (null for scratch) as the key
        const cached = loadDiscoverState(activeProfileId)
        if (cached && cached.selectedBrandIds && cached.selectedBrandIds.length > 0) {
            const cachedBrands = allBrands.filter(b => cached.selectedBrandIds.includes(b.id))
            if (cachedBrands.length > 0) {
                setSelectedBrands(cachedBrands)
                if (cached.hasSearched && cached.recommendationIds) {
                    const cachedRecs = cached.recommendationIds
                        .map(id => {
                            const brand = allBrands.find(b => b.id === id)
                            return brand ? { brand, score: cached.scores?.[id] || 0 } : null
                        })
                        .filter(Boolean)
                    setRecommendations(cachedRecs)
                    setHasSearched(true)
                }
                restoredFromCache.current = true
                return
            }
        }

        // No cache — for scratch mode start empty, for profile load saved brands
        if (!activeProfileId || !loadedForProfileId) return
        if (loadedForProfileId !== activeProfileId) return
        const profileBrands = allBrands.filter(b => likedBrandIds.includes(b.id))
        setSelectedBrands(profileBrands)
    }, [allBrands, activeProfileId, loadedForProfileId, likedBrandIds])

    // Save discover state to localStorage whenever it changes
    useEffect(() => {
        if (!hasMountedRef.current) return
        saveDiscoverState(activeProfileId, selectedBrands, recommendations, hasSearched)
    }, [selectedBrands, recommendations, hasSearched, activeProfileId])

    // Immediately clear local state when profile changes while on this page
    const prevProfileId = useRef(activeProfileId)
    useEffect(() => {
        if (prevProfileId.current !== activeProfileId) {
            prevProfileId.current = activeProfileId
            restoredFromCache.current = false
            setSelectedBrands([])
            setRecommendations([])
            setHasSearched(false)
            setBrandsAddedSinceSearch(0)
        }
    }, [activeProfileId])

    // React to brand load completions — repopulate after profile brands finish loading
    useEffect(() => {
        if (brandsLoadVersion === lastHandledVersion.current) return
        lastHandledVersion.current = brandsLoadVersion

        if (allBrands.length === 0) return
        // Don't overwrite if we restored from cache on mount
        if (restoredFromCache.current) {
            restoredFromCache.current = false
            return
        }

        // Start from scratch — no active profile
        if (!activeProfileId || !loadedForProfileId) {
            return
        }

        // Profile brands finished loading — populate selected brands
        if (loadedForProfileId === activeProfileId) {
            const profileBrands = allBrands.filter(b => likedBrandIds.includes(b.id))
            setSelectedBrands(profileBrands)
            setHasSearched(false)
            setRecommendations([])
            setBrandsAddedSinceSearch(0)
        }
    }, [brandsLoadVersion, allBrands])

    useEffect(() => {
        if (searchTerm.trim().length < 1) {
            setSearchResults([])
            return
        }
        const results = allBrands.filter(b =>
            b.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
            !selectedBrands.find(s => s.id === b.id)
        ).slice(0, 6)
        setSearchResults(results)
    }, [searchTerm, allBrands, selectedBrands])

    // Watch for dislike changes and increment refresh counter
    const prevDislikedCount = useRef(dislikedBrandIds.length)
    useEffect(() => {
        if (!hasSearched) {
            prevDislikedCount.current = dislikedBrandIds.length
            return
        }
        if (dislikedBrandIds.length !== prevDislikedCount.current) {
            prevDislikedCount.current = dislikedBrandIds.length
            setBrandsAddedSinceSearch(prev => prev + 1)
        }
    }, [dislikedBrandIds, hasSearched])

    const fetchRecommendations = () => {
        if (selectedBrands.length < 1) return
        setLoading(true)
        setHasSearched(true)
        setBrandsAddedSinceSearch(0)
        const ids = selectedBrands.map(b => b.id)
        const shouldIncludeDefault = includeDefaultDislikes && activeProfile && !activeProfile.is_default
        getRecommendations(ids, dislikedBrandIds, shouldIncludeDefault, user?.id || '').then(recs => {
            setRecommendations(recs || [])
            setLoading(false)
            track('recommendations_viewed', {
                result_count: recs?.length || 0,
                input_count: ids.length
            })
        })
    }

    const attrSelectionCount =
        Object.values(attrAxes).filter(Boolean).length +
        attrThemes.filter(Boolean).length

    const fetchByAttributes = () => {
        if (attrSelectionCount < 1) return
        setAttrLoading(true)
        setAttrHasSearched(true)
        const axes = Object.fromEntries(Object.entries(attrAxes).filter(([, v]) => v))
        const themes = attrThemes.filter(Boolean)
        track('attributes_search', { axis_count: Object.keys(axes).length, theme_count: themes.length })
        getRecommendationsByAttributes(axes, themes).then(recs => {
            setAttrResults(recs || [])
            setAttrLoading(false)
            track('recommendations_viewed', {
                result_count: recs?.length || 0,
                input_count: attrSelectionCount,
                mode: 'attributes'
            })
        })
    }

    const setAttrAxis = (field, value) => {
        setAttrAxes(prev => {
            const next = { ...prev }
            if (value) next[field] = value
            else delete next[field]
            return next
        })
    }

    const setAttrThemeAt = (idx, value) => {
        setAttrThemes(prev => prev.map((t, i) => (i === idx ? value : t)))
    }

    const clearAttributes = () => {
        setAttrAxes({})
        setAttrThemes(['', '', ''])
        setAttrHasSearched(false)
        setAttrResults([])
    }

    const addBrand = (brand) => {
        if (selectedBrands.find(b => b.id === brand.id)) return
        track('brand_selected')
        setSelectedBrands(prev => [...prev, brand])
        setSearchTerm('')
        setSearchResults([])
        if (hasSearched) {
            setBrandsAddedSinceSearch(prev => prev + 1)
        }
    }

    const removeBrand = (brandId) => {
        setSelectedBrands(prev => {
            const updated = prev.filter(b => b.id !== brandId)
            if (updated.length === 0) {
                setHasSearched(false)
                setRecommendations([])
                setBrandsAddedSinceSearch(0)
            }
            return updated
        })
        if (hasSearched) {
            setBrandsAddedSinceSearch(prev => prev + 1)
        }
    }

    const clearAll = () => {
        setSelectedBrands([])
        setRecommendations([])
        setHasSearched(false)
        setBrandsAddedSinceSearch(0)
    }

    const handleSaveToProfile = async () => {
        if (!activeProfileId || selectedBrands.length === 0) return
        const ids = selectedBrands.map(b => b.id)
        await saveBrandsToProfile(ids)
        const profileName = activeProfile?.is_default ? 'My Brands' : activeProfile?.name
        setSaveMessage(`Saved ${ids.length} brands to ${profileName}`)
        setTimeout(() => setSaveMessage(''), 3000)
    }

    const handleAddRecommendationToSelected = (brand) => {
        if (!selectedBrands.find(b => b.id === brand.id)) {
            setSelectedBrands(prev => [...prev, brand])
            setRecommendations(prev => prev.filter(r => r.brand.id !== brand.id))
            setBrandsAddedSinceSearch(prev => prev + 1)
        }
    }

    const handleSaveToSpecificProfile = async (profileId, profileName) => {
        if (!user || selectedBrands.length === 0) return
        setShowSavePicker(false)
        for (const brand of selectedBrands) {
            await addProfileBrand(profileId, brand.id, 'liked')
        }
        await reloadProfileBrands(profileId)
        setSaveMessage(`Saved ${selectedBrands.length} brands to "${profileName}"`)
        setTimeout(() => setSaveMessage(''), 3000)
    }

    const handleSaveAsNewProfile = () => {
        if (!user || selectedBrands.length === 0) return
        if (!canCreateCustomProfile) {
            setSaveMessage('You already have 2 custom profiles. Delete one to create a new one.')
            setTimeout(() => setSaveMessage(''), 4000)
            return
        }
        setNewProfileName('')
        setNewProfileError('')
        setShowNewProfileModal(true)
    }

    const confirmSaveAsNewProfile = async () => {
        const name = newProfileName.trim()
        if (!name) return
        setNewProfileError('')
        try {
            const result = await createProfile(name)
            if (result?.profile) {
                const newProfileId = result.profile.id
                for (const brand of selectedBrands) {
                    await addProfileBrand(newProfileId, brand.id, 'liked')
                }
                setShowNewProfileModal(false)
                setNewProfileName('')
                setSaveMessage(`Saved ${selectedBrands.length} brands to "${name}"`)
                setTimeout(() => setSaveMessage(''), 3000)
            }
        } catch (e) {
            setNewProfileError(e.message || 'Failed to create profile')
        }
    }

    const handleNewProfileKeyDown = (e) => {
        if (e.key === 'Enter') confirmSaveAsNewProfile()
        if (e.key === 'Escape') { setShowNewProfileModal(false); setNewProfileName(''); setNewProfileError('') }
    }

    const COLD_START_PAGE_SIZE = 15
    const COLD_START_MORE_SIZE = 10

    const COLD_START_VISIBLE = COLD_START_PAGE_SIZE

    const isExcludedByTheme = (brand) => {
        if (!excludedThemes.length) return false
        const isOverride = likedBrandIds.includes(brand.id) || selectedBrands.some(b => b.id === brand.id)
        if (isOverride) return false
        return excludedThemes.some(theme => {
            const tags = filterGroups[theme] || []
            return tags.includes(brand.cultural_origin_primary) || tags.includes(brand.cultural_origin_secondary)
        })
    }

    const getColdStartOrder = (theme) => {
        if (!coldStartOrderRef.current[theme]) {
            if (theme === EXPLORE) {
                const eligible = allBrands.filter(b => b.tier !== 'Mainstream')
                coldStartOrderRef.current[theme] = dailyShuffle(eligible)
            } else if (theme === NEWLY_ADDED) {
                const ts = b => (b.created_at ? new Date(b.created_at).getTime() : 0)
                const sorted = [...allBrands].sort((a, b) => ts(b) - ts(a))
                coldStartOrderRef.current[theme] = sorted.slice(0, NEWLY_ADDED_COUNT)
            } else {
                const tags = filterGroups[theme] || []
                const inTheme = allBrands.filter(b =>
                    tags.includes(b.cultural_origin_primary) || tags.includes(b.cultural_origin_secondary)
                )
                coldStartOrderRef.current[theme] = [...inTheme].sort((a, b) => a.name.localeCompare(b.name))
            }
        }
        return coldStartOrderRef.current[theme]
    }

    // Initialise or replenish the visible cold start list
    const replenishColdStart = (theme, currentVisible, currentSelected, targetCount) => {
        const ordered = getColdStartOrder(theme)
        const selectedIds = new Set(currentSelected.map(b => b.id))
        const visibleIds = new Set(currentVisible.map(b => b.id))

        // Keep existing visible brands that haven't been selected
        const kept = currentVisible.filter(b => !selectedIds.has(b.id))

        // Fill up to targetCount from the ordered pool
        const toAdd = ordered.filter(b => !selectedIds.has(b.id) && !visibleIds.has(b.id))
        const needed = targetCount - kept.length
        const added = toAdd.slice(0, Math.max(0, needed))

        return [...kept, ...added]
    }

    // Initialise cold start when theme or brands load
    useEffect(() => {
        if (!activeTheme || allBrands.length === 0) return
        coldStartOrderRef.current[activeTheme] = null // reset cache when theme changes
        setVisibleColdStart(replenishColdStart(activeTheme, [], selectedBrands, COLD_START_VISIBLE))
        setColdStartPage(1)
    }, [activeTheme, allBrands.length])

    // Replenish when a brand is selected — append new brand at the end
    useEffect(() => {
        if (!activeTheme || allBrands.length === 0) return
        setVisibleColdStart(prev => replenishColdStart(activeTheme, prev, selectedBrands, COLD_START_VISIBLE + (coldStartPage - 1) * COLD_START_MORE_SIZE))
    }, [selectedBrands.length])

    const getAvailableThemes = () => Object.keys(filterGroups).sort((a, b) => a.localeCompare(b))
    const getThemeDiscoveryBrands = () => {
        if (selectedThemes.length === 0) return []

        const cacheKey = selectedThemes.slice().sort().join('|')

        if (!themeBrandOrderRef.current[cacheKey]) {
            const ordered = allBrands
                .map(b => {
                    const matches = selectedThemes.filter(theme => {
                        const tags = filterGroups[theme] || []
                        return tags.includes(b.cultural_origin_primary) || tags.includes(b.cultural_origin_secondary)
                    }).length
                    return { brand: b, matches }
                })
                .filter(({ matches }) => matches > 0)
                .sort((a, b) => {
                    if (b.matches !== a.matches) return b.matches - a.matches
                    return a.brand.name.localeCompare(b.brand.name)
                })
                .map(({ brand }) => brand)
            themeBrandOrderRef.current[cacheKey] = ordered
        }

        return themeBrandOrderRef.current[cacheKey]
    }

    const handleThemeAddToSelected = (brand) => {
        if (!selectedBrands.find(b => b.id === brand.id)) {
            setSelectedBrands(prev => [...prev, brand])
        }
    }

    const handleThemeFindRecs = () => {
        setDiscoverMode('brands')
        fetchRecommendations()
    }

    const canSearch = selectedBrands.length >= 1
    const showDefaultDislikesToggle = user && activeProfile && !activeProfile.is_default && defaultProfile
    const profileName = activeProfile?.is_default ? 'My Brands' : activeProfile?.name
    const isScratch = !activeProfileId
    const showSaveAsProfile = user && isScratch && selectedBrands.length > 0

    const brandCount = allBrands.length > 0 ? Math.floor(allBrands.length / 25) * 25 : null

    return (
        <div className="discover-page">
            <div className="discover-header">
                <h1>Discover New Brands</h1>
                <p>
                    Find your next favorite menswear brand
                    {brandCount ? ` from ${brandCount}+ curated labels` : ''} and track what's available on resale, refreshed daily.
                </p>
            </div>

            {/* Mode toggle */}
            <div className="discover-mode-toggle">
                <button
                    className={`mode-btn ${discoverMode === 'brands' ? 'active' : ''}`}
                    onClick={() => setDiscoverMode('brands')}
                >
                    By Brand
                </button>
                <button
                    className={`mode-btn ${discoverMode === 'style' ? 'active' : ''}`}
                    onClick={() => setDiscoverMode('style')}
                >
                    By Style
                </button>
            </div>

            {/* ── By Style: find by attributes ── */}
            {discoverMode === 'style' && (
                <div className="attr-finder open">
                    <div className="attr-finder-header-bar">
                        <h2>Search for the style you want</h2>
                    </div>
                    <div className="attr-finder-body">
                        <div className="attr-finder-section-label">Themes</div>
                        <div className="attr-theme-row">
                            {[0, 1, 2].map(idx => (
                                <select
                                    key={idx}
                                    className="attr-select"
                                    value={attrThemes[idx]}
                                    onChange={e => setAttrThemeAt(idx, e.target.value)}
                                >
                                    <option value="">Any theme</option>
                                    {Object.keys(filterGroups).sort().map(theme => (
                                        <option key={theme} value={theme}>{theme}</option>
                                    ))}
                                </select>
                            ))}
                        </div>

                        <div className="attr-finder-section-label">Style</div>
                        <div className="attr-axis-row">
                            {ATTR_AXES.map(axis => (
                                <div key={axis.field} className="attr-axis-item">
                                    <label className="attr-axis-label">{axis.label}</label>
                                    <select
                                        className="attr-select"
                                        value={attrAxes[axis.field] || ''}
                                        onChange={e => setAttrAxis(axis.field, e.target.value)}
                                    >
                                        <option value="">Any</option>
                                        {axis.options.map(([val, lbl]) => (
                                            <option key={val} value={val}>{lbl}</option>
                                        ))}
                                    </select>
                                </div>
                            ))}
                        </div>

                        <div className="attr-finder-actions">
                            <button
                                className="attr-find-btn"
                                onClick={fetchByAttributes}
                                disabled={attrSelectionCount < 1}
                            >
                                {attrSelectionCount < 1 ? 'Pick at least one preference' : 'Find brands for me'}
                            </button>
                            {attrSelectionCount > 0 && (
                                <button className="attr-clear-btn" onClick={clearAttributes}>Clear</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* By Style results / browse — entirely from attr state */}
            {discoverMode === 'style' && (
                attrHasSearched ? (
                    <div className="recommendations">
                        <div className="recommendations-header">
                            <h2>Your style matches</h2>
                            <p>{attrResults.length} brands matched to what you're looking for — save the ones you like</p>
                        </div>
                        {attrLoading ? (
                            <div className="loading">Finding your matches...</div>
                        ) : (
                            <div className="brand-grid">
                                {attrResults.map((rec, index) => (
                                    <BrandCard
                                        key={rec.brand.id}
                                        brand={rec.brand}
                                        score={rec.score}
                                        rank={index + 1}
                                    />
                                ))}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="recommendations">
                        <div className="recommendations-header">
                            <h2>Explore the index</h2>
                            <p>Browse below, or use the style search above to find matches</p>
                        </div>
                        <div className="brand-grid">
                            {getColdStartOrder(EXPLORE).map(brand => (
                                <BrandCard key={brand.id} brand={brand} />
                            ))}
                        </div>
                    </div>
                )
            )}

            {discoverMode === 'brands' && excludedThemes.length > 0 && (
                <div className="excluded-themes-note">
                    Hiding {excludedThemes.join(', ')} — saved and selected brands always shown
                </div>
            )}

            {discoverMode === 'brands' && <div className="discover-search">
                <div className="discover-search-row">
                <input
                    type="text"
                    placeholder="Search a brand you know..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="search-input"
                />
                <div className="hide-themes-wrap" ref={hideThemesRef}>
                    <button
                        type="button"
                        className={`hide-themes-btn${excludedThemes.length ? ' has-exclusions' : ''}`}
                        onClick={() => setHideThemesOpen(o => !o)}
                    >
                        Hide themes{excludedThemes.length ? ` (${excludedThemes.length})` : ''}
                    </button>
                    {hideThemesOpen && (
                        <div className="hide-themes-menu">
                            {Object.keys(filterGroups).sort().map(theme => (
                                <label key={theme} className="hide-themes-option">
                                    <input
                                        type="checkbox"
                                        checked={excludedThemes.includes(theme)}
                                        onChange={() => {
                                            setExcludedThemes(prev =>
                                                prev.includes(theme)
                                                    ? prev.filter(t => t !== theme)
                                                    : [...prev, theme]
                                            )
                                        }}
                                    />
                                    {theme}
                                </label>
                            ))}
                            {excludedThemes.length > 0 && (
                                <button
                                    className="hide-themes-clear"
                                    onClick={() => setExcludedThemes([])}
                                >
                                    Clear exclusions
                                </button>
                            )}
                        </div>
                    )}
                </div>
                </div>
                {searchTerm.trim() && searchResults.length === 0 && (
                    <button type="button" className="suggest-search-hint" onClick={() => setShowSuggest(true)}>
                        Don't see "{searchTerm.trim()}"? Suggest it
                    </button>
                )}
                {searchResults.length > 0 && (
                    <div className="search-dropdown">
                        {searchResults.map(brand => (
                            <div
                                key={brand.id}
                                className="search-result"
                                onClick={() => addBrand(brand)}
                            >
                                {brand.name}
                            </div>
                        ))}
                    </div>
                )}
            </div>}

            {discoverMode === 'brands' && selectedBrands.length > 0 && (
                <div className="selected-section">
                    <div className="selected-header">
                        <span className="selected-count">{selectedBrands.length} brand{selectedBrands.length !== 1 ? 's' : ''} selected</span>
                        <button className="clear-btn" onClick={clearAll}>Clear all</button>
                    </div>
                    <div className="selected-brands">
                        {selectedBrands.map(brand => (
                            <span key={brand.id} className="selected-pill">
                                {brand.name}
                                <button onClick={() => removeBrand(brand.id)}>×</button>
                            </span>
                        ))}
                    </div>
                    {showDefaultDislikesToggle && (
                        <label className="default-dislikes-toggle">
                            <input
                                type="checkbox"
                                checked={includeDefaultDislikes}
                                onChange={e => setIncludeDefaultDislikes(e.target.checked)}
                            />
                            <span>Include My Brands dislikes</span>
                        </label>
                    )}
                    <div className="discover-actions">
                        <button
                            className={`go-btn ${canSearch ? 'active' : 'disabled'}`}
                            onClick={fetchRecommendations}
                            disabled={!canSearch}
                        >
                            {!canSearch
                                ? 'Add a brand to get started'
                                : hasSearched && brandsAddedSinceSearch > 0
                                    ? `Refresh recommendations (${brandsAddedSinceSearch} change${brandsAddedSinceSearch !== 1 ? 's' : ''})`
                                    : 'Find brands for me'
                            }
                        </button>
                        {user && selectedBrands.length > 0 && (
                            <div className="save-picker-wrapper">
                                <button
                                    className="save-to-profile-btn"
                                    onClick={() => setShowSavePicker(prev => !prev)}
                                >
                                    Save selected ▾
                                </button>
                                {showSavePicker && (
                                    <div className="save-picker-dropdown">
                                        {profiles.map(p => (
                                            <button
                                                key={p.id}
                                                className="save-picker-option"
                                                onClick={() => handleSaveToSpecificProfile(p.id, p.is_default ? 'My Brands' : p.name)}
                                            >
                                                {p.is_default ? 'My Brands' : p.name}
                                            </button>
                                        ))}
                                        {canCreateCustomProfile && (
                                            <button
                                                className="save-picker-option save-picker-new"
                                                onClick={() => { setShowSavePicker(false); handleSaveAsNewProfile() }}
                                            >
                                                + Create new profile
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    {saveMessage && <p className="save-message">{saveMessage}</p>}
                </div>
            )}

            {!hasSearched && discoverMode === 'brands' && (
                <div className="cold-start">
                    <p className="cold-start-label">
                        {selectedBrands.length === 0
                            ? 'Browse brands below, or pick a theme | add brands you know to get recommendations:'
                            : 'Add more brands to refine your results:'
                        }
                    </p>
                    <div className="tier-tabs">
                        <button
                            key={EXPLORE}
                            className={`tier-tab ${activeTheme === EXPLORE ? 'active' : ''}`}
                            onClick={() => setActiveTheme(EXPLORE)}
                        >
                            {EXPLORE}
                        </button>
                        <button
                            key={NEWLY_ADDED}
                            className={`tier-tab ${activeTheme === NEWLY_ADDED ? 'active' : ''}`}
                            onClick={() => setActiveTheme(NEWLY_ADDED)}
                        >
                            {NEWLY_ADDED}
                        </button>
                        {Object.keys(filterGroups).sort().map(theme => (
                            <button
                                key={theme}
                                className={`tier-tab ${activeTheme === theme ? 'active' : ''}`}
                                onClick={() => setActiveTheme(theme)}
                            >
                                {theme}
                            </button>
                        ))}
                    </div>
                    <p className="cold-start-search-hint">
                        Looking for a specific brand? Use the search bar above.
                    </p>
                </div>
            )}

            {/* By Brand browse cards — only when brand tab hasn't searched */}
            {!hasSearched && discoverMode === 'brands' && (
                <div className="recommendations">
                    <div className="recommendations-header">
                        <h2>
                            {activeTheme === EXPLORE
                                ? 'Explore the index'
                                : activeTheme === NEWLY_ADDED
                                    ? 'Newly added brands'
                                    : activeTheme}
                        </h2>
                        <p>
                            {activeTheme === EXPLORE
                                ? 'Browse the collection | add brands you know to get recommendations'
                                : activeTheme === NEWLY_ADDED
                                    ? 'Recently added to the index | add brands you know to get recommendations'
                                    : `${getColdStartOrder(activeTheme).length} brands in ${activeTheme} | add ones you know to get recommendations`}
                        </p>
                    </div>
                    <div className="brand-grid">
                        {getColdStartOrder(activeTheme).filter(b => !isExcludedByTheme(b)).map(brand => (
                            <div key={brand.id} className="recommendation-card-wrapper">
                                <BrandCard brand={brand} />
                                <button
                                    className={`add-to-selected-btn ${selectedBrands.find(b => b.id === brand.id) ? 'added' : ''}`}
                                    onClick={() => addBrand(brand)}
                                    disabled={!!selectedBrands.find(b => b.id === brand.id)}
                                >
                                    {selectedBrands.find(b => b.id === brand.id) ? '✓ Added' : '+ Add to selected'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* By Brand recommendations */}
            {hasSearched && discoverMode === 'brands' && (
                <div className="recommendations">
                    <div className="recommendations-header">
                        <h2>Recommended for you</h2>
                        <p>{recommendations.length} brands matched to your taste{selectedBrands.length < 3 ? ' — add more brands to refine results' : ''}</p>
                    </div>
                    {loading ? (
                        <div className="loading">Finding your matches...</div>
                    ) : (
                        <div className="brand-grid">
                            {recommendations.filter(rec => !isExcludedByTheme(rec.brand)).map((rec, index) => (
                                <div key={rec.brand.id} className="recommendation-card-wrapper">
                                    <BrandCard
                                        brand={rec.brand}
                                        score={rec.score}
                                        rank={index + 1}
                                    />
                                    <button
                                        className="add-to-selected-btn"
                                        onClick={() => handleAddRecommendationToSelected(rec.brand)}
                                    >
                                        + Add to selected
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {showNewProfileModal && (
                <div className="delete-modal-backdrop" onClick={() => { setShowNewProfileModal(false); setNewProfileName(''); setNewProfileError('') }}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <p className="delete-modal-text">Save as new profile</p>
                        <p className="delete-modal-sub">Name your profile — e.g. Outdoor, Office, Casual. Your {selectedBrands.length} selected brand{selectedBrands.length !== 1 ? 's' : ''} will be saved to it.</p>
                        <input
                            type="text"
                            placeholder="Profile name"
                            value={newProfileName}
                            onChange={e => setNewProfileName(e.target.value)}
                            onKeyDown={handleNewProfileKeyDown}
                            className="create-profile-modal-input"
                            autoFocus
                        />
                        {newProfileError && <p className="create-profile-modal-error">{newProfileError}</p>}
                        <div className="delete-modal-actions">
                            <button className="delete-confirm-no" onClick={() => { setShowNewProfileModal(false); setNewProfileName(''); setNewProfileError('') }}>Cancel</button>
                            <button className="delete-confirm-yes" onClick={confirmSaveAsNewProfile}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        {showSuggest && (
                <SuggestBrandModal
                    initialBrandName={searchTerm}
                    onClose={() => setShowSuggest(false)}
                />
            )}
        </div>
    )
}

export default Discover
